// Localização: frontend/src/components/Prontuario/PeriodoFilterButton.jsx
// Componente reutilizável para botão de filtro de período (padronizado)

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Calendar } from 'lucide-react';
import FloatingCalendarFilter from '../Financeiro/FloatingCalendarFilter';

/**
 * Componente de Botão de Filtro de Período
 * Padronizado para uso em todas as abas do prontuário
 * 
 * @param {Object} props
 * @param {string} props.periodo - Período selecionado (ex: 'hoje', 'semana', 'mes')
 * @param {string} props.startDate - Data inicial (formato YYYY-MM-DD)
 * @param {string} props.endDate - Data final (formato YYYY-MM-DD)
 * @param {Function} props.onPeriodoChange - Callback quando o período muda
 * @param {Function} props.onDateRangeChange - Callback quando o intervalo de datas muda (startDate, endDate)
 * @param {string} props.className - Classes CSS adicionais
 */
const PeriodoFilterButton = ({
    periodo = '',
    startDate = '',
    endDate = '',
    onPeriodoChange,
    onDateRangeChange,
    className = ''
}) => {
    const [showPeriodFilter, setShowPeriodFilter] = useState(false);
    const periodButtonRef = useRef(null);
    const periodFilterRef = useRef(null);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleClickOutside = (event) => {
            if (
                periodFilterRef.current &&
                periodButtonRef.current &&
                !periodFilterRef.current.contains(event.target) &&
                !periodButtonRef.current.contains(event.target)
            ) {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        document.addEventListener('touchstart', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
            document.removeEventListener('touchstart', handleClickOutside);
        };
    }, [showPeriodFilter]);

    // Fechar dropdown ao pressionar Escape
    useEffect(() => {
        if (!showPeriodFilter) return;

        const handleEscape = (event) => {
            if (event.key === 'Escape') {
                setShowPeriodFilter(false);
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => {
            document.removeEventListener('keydown', handleEscape);
        };
    }, [showPeriodFilter]);

    const handlePeriodoChange = useCallback((newPeriodo) => {
        if (onPeriodoChange) {
            onPeriodoChange(newPeriodo || '');
        }
    }, [onPeriodoChange]);

    const handleDateRangeChange = useCallback((newStartDate, newEndDate) => {
        if (onDateRangeChange) {
            // Evitar chamadas com valores duplicados
            const startDateStr = newStartDate || '';
            const endDateStr = newEndDate || '';
            
            // Só chamar se os valores realmente mudaram
            if (startDate !== startDateStr || endDate !== endDateStr) {
                onDateRangeChange(startDateStr, endDateStr);
            }
        }
    }, [onDateRangeChange, startDate, endDate]);

    return (
        <div className={`relative ${className}`} ref={periodButtonRef}>
            <button
                onClick={() => setShowPeriodFilter(!showPeriodFilter)}
                className={`
                    flex items-center justify-center gap-2
                    px-3 py-2 sm:px-4 sm:py-2.5 rounded-lg
                    transition-all duration-200 font-medium 
                    text-xs sm:text-sm
                    border shadow-sm hover:shadow-md
                    ${showPeriodFilter 
                        ? 'bg-gray-100 text-gray-700 border-gray-300 border-[0.5px] shadow-md font-semibold' 
                        : 'bg-white text-gray-600 border-gray-300 hover:border-gray-300 hover:bg-gray-50'
                    }
                `}
                title={showPeriodFilter ? 'Ocultar filtro de período' : 'Mostrar filtro de período'}
            >
                <Calendar className={`h-4 w-4 flex-shrink-0 ${showPeriodFilter ? 'scale-110' : ''} transition-transform`} />
                <span className="hidden sm:inline whitespace-nowrap">Período</span>
            </button>

            {/* Dropdown do Filtro de Calendário */}
            {showPeriodFilter && (
                <div
                    ref={periodFilterRef}
                    className="absolute top-full right-0 mt-2 w-[600px] max-w-[calc(100vw-2rem)] sm:max-w-[600px] bg-white rounded-xl shadow-2xl border border-gray-200 z-50 overflow-hidden animate-fadeIn"
                    style={{
                        maxHeight: '80vh',
                        overflowY: 'auto'
                    }}
                >
                    <div className="p-3 sm:p-4">
                        <FloatingCalendarFilter
                            periodo={periodo}
                            onPeriodoChange={handlePeriodoChange}
                            startDate={startDate}
                            endDate={endDate}
                            onDateRangeChange={handleDateRangeChange}
                            showStatus={false}
                            noNegativeMargin={true}
                            customZIndex="z-0"
                            isInDropdown={true}
                            showOnlyContent={true}
                        />
                    </div>
                </div>
            )}
        </div>
    );
};

export default PeriodoFilterButton;

